import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sparkles,
  Lightbulb,
  Gift,
  Plus,
  PlayCircle,
  LucideIcon,
} from "lucide-react";
import { UserContext } from "components/context/UserContext";
import MyRepCaresPanel from "components/common/myRepCares/MyRepCaresPanel";
import ReferralsButton from "components/rep/referral/ReferralsButton";
import "./FabMenu.css";

declare const Supademo: JObj;

interface FabMenuProps {
  showFab: boolean;
  showSupademos: boolean;
  isRep: boolean;
}

const FabMenu: React.FC<FabMenuProps> = ({ showFab, showSupademos, isRep }) => {
  const { state } = useContext(UserContext)!;

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [caresOpen, setCaresOpen] = useState<boolean>(false);
  const [referralsOpen, setReferralsOpen] = useState<boolean>(false);
  const fabRef = useRef<HTMLDivElement>(null);

  // Hidden unless the server says this rep may use the referral program.
  const canRefer = useMemo(
    () => isRep && state?.currentUser?.isReferralEligible === true,
    [isRep, state?.currentUser?.isReferralEligible],
  );

  const supaDemoKey = useMemo(
    () =>
      state?.currentUser?.roleId === 8 || state?.currentUser?.roleId === 2
        ? process.env.REACT_APP_OFFICE_SUPADEMO_KEY
        : process.env.REACT_APP_REPS_SUPADEMO_KEY,
    [state?.currentUser],
  );

  const handleOpenSupademo = useCallback(() => {
    if (!supaDemoKey) return;
    try {
      Supademo?.widget?.open(`${supaDemoKey}`);
    } catch (e) {
      console.warn("Failed to open Supademo:", e);
    }
  }, [supaDemoKey]);

  const handleOpenCares = useCallback(() => {
    setMenuOpen(false);
    setCaresOpen(true);
  }, []);

  const handleOpenReferrals = useCallback(() => {
    setMenuOpen(false);
    setReferralsOpen(true);
  }, []);

  const handleOpenDemo = useCallback(() => {
    setMenuOpen(false);
    handleOpenSupademo();
  }, [handleOpenSupademo]);

  // Build the list of speed-dial menu items based on feature flags
  const menuItems = useMemo(() => {
    const items: Array<{
      key: string;
      label: string;
      Icon: LucideIcon;
      onClick: () => void;
      tone: "blue" | "white" | "accent";
    }> = [];

    if (showSupademos && supaDemoKey) {
      items.push({
        key: "supademo",
        label: "See How To Use MyRep",
        Icon: PlayCircle,
        onClick: handleOpenDemo,
        tone: "accent",
      });
    }
    if (showFab) {
      items.push({
        key: "cares",
        label: "MyRep Cares",
        Icon: Lightbulb,
        onClick: handleOpenCares,
        tone: "blue",
      });
    }
    if (canRefer && showFab) {
      items.push({
        key: "referrals",
        label: "Referrals",
        Icon: Gift,
        onClick: handleOpenReferrals,
        tone: "white",
      });
    }
    return items;
  }, [
    showSupademos,
    supaDemoKey,
    showFab,
    canRefer,
    handleOpenDemo,
    handleOpenCares,
    handleOpenReferrals,
  ]);

  // Close the expanded menu when clicking outside (on the backdrop)
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Belt-and-suspenders click-outside handling: the backdrop button below
  // covers the viewport visually, but a document-level listener (the pattern
  // already used by NotificationsMenu) guarantees the menu closes even if
  // some other fixed/high-z-index element on the page happens to intercept
  // the backdrop's own click.
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (fabRef.current && !fabRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <>
      {/*
        These child components are always mounted (trigger button hidden) so
        that their internal drawers / modals persist independently of the
        speed-dial menu's open/closed state.
      */}
      <div className="fab-container" aria-hidden="true">
        {showFab && (
          <MyRepCaresPanel
            hideTrigger
            isOpen={caresOpen}
            onOpenChange={setCaresOpen}
          />
        )}
        {canRefer && showFab && (
          <ReferralsButton
            hideTrigger
            isOpen={referralsOpen}
            onOpenChange={setReferralsOpen}
          />
        )}
      </div>

      {/* Single FAB that expands into a labeled speed-dial menu */}
      {menuItems.length > 0 && (
        <>
          <AnimatePresence>
            {menuOpen && (
              <motion.button
                key="fab-backdrop"
                className="fab-menu-backdrop"
                aria-label="Close quick actions"
                onClick={closeMenu}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              />
            )}
          </AnimatePresence>

          <div className="fab-compact" ref={fabRef}>
            <AnimatePresence>
              {menuOpen && (
                <motion.ul
                  key="fab-menu"
                  className="fab-menu-list"
                  initial={{ opacity: 0, y: 12, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 12, scale: 0.96 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {menuItems.map((item, idx) => (
                    <motion.li
                      key={item.key}
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 16 }}
                      transition={{
                        duration: 0.18,
                        delay: menuOpen ? idx * 0.04 : 0,
                      }}
                    >
                      <button
                        type="button"
                        className={`fab-menu-item fab-menu-item--${item.tone}`}
                        onClick={item.onClick}
                      >
                        <span
                          className="fab-menu-item__icon"
                          aria-hidden="true"
                        >
                          <item.Icon size={18} />
                        </span>
                        <span className="fab-menu-item__label">
                          {item.label}
                        </span>
                      </button>
                    </motion.li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>

            <motion.button
              type="button"
              className={`fab-main-btn ${menuOpen ? "fab-main-btn--open" : ""}`}
              onClick={() => setMenuOpen((p) => !p)}
              aria-label={
                menuOpen ? "Close quick actions" : "Open quick actions"
              }
              aria-expanded={menuOpen}
              whileTap={{ scale: 0.92 }}
            >
              <motion.span
                className="fab-main-btn__icon"
                animate={{ rotate: menuOpen ? 45 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {menuOpen ? <Plus size={22} /> : <Sparkles size={20} />}
              </motion.span>
            </motion.button>
          </div>
        </>
      )}
    </>
  );
};

export default FabMenu;
